// src/lib/procurementCalc.js
// 102R — ONE home for the procurement money formulas (M10 canonical + GL account math).
// Every tab imports these instead of keeping a local copy (the ~18 byte-identical sites
// consolidated here; the disguised local predicates are deleted at their call sites).
//
// Sources of truth:
//   - M10_PROCUREMENT_FORMULAS.md (finance-leg op formulas, overdue predicate)
//   - balance() / computeAccountNets (server) — the GL account balance the AP/AR/GIT
//     tiles read via GET /api/engine/balances; the ledger builders here reproduce the
//     SAME leg math per row so a ledger's final running balance == balance(account).

import { paymentMethodOf } from './paymentMethod.js';   // explicit extension: node --test loads this file
import { receivablePosition } from './customerPosition.js';

// ── Due date / overdue (M10: cash rows — payment_days IS NULL — are NEVER overdue) ──
export function dueDateOf(row) {
  if (!row?.date || row.payment_days == null) return null;
  return new Date(new Date(row.date).getTime() + row.payment_days * 86400000).toISOString().slice(0, 10);
}

export function isOverdueRow(row, todayStr) {
  if (row.type !== 'Purchase' || row.status !== 'Pending') return false;
  if (!row.date || row.payment_days == null) return false;
  const due = dueDateOf(row);
  return due != null && due < todayStr;
}

// Overdue KPI (M10 OVERDUE KPI DISPLAY): value = Σ amount over overdue rows,
// rowCount = overdue payment rows, opCount = distinct operations. (Was ProcurementPage F2.)
export function computeOverdueKpi(financeData, todayStr) {
  let value = 0;
  let rowCount = 0;
  const opIds = new Set();
  for (const row of (financeData || [])) {
    if (!isOverdueRow(row, todayStr)) continue;
    value += Number(row.amount) || 0;
    rowCount++;
    opIds.add(row.operation_id);
  }
  return { overdueValue: value, overdueRowCount: rowCount, overdueOpCount: opIds.size };
}

// Overdue op set (M10 overdue filter — was GitTab overdueOpIds).
export function buildOverdueOpIds(financeData, todayStr) {
  const s = new Set();
  for (const row of (financeData || [])) {
    if (isOverdueRow(row, todayStr)) s.add(row.operation_id);
  }
  return s;
}

// ── Goods-base GIT membership (Model A holding-safe) ───────────────────────────────
// An account is goods-base GIT IFF its ROLE matches /^git_(\w+)$/ (git_feed, git_eggs,
// git_medicine, git_birds, git_droppings). This mirrors the backend K6/valuation regex
// and the 10091/11357 repoint. The Model A account GIT: Landed Cost Holding has
// type='git' but role='lc_holding' → fails the regex → EXCLUDED, so an unallocated
// landed cost sitting in holding does NOT inflate procurement GIT tiles (D3). The role
// (account_role from the backend column; tolerant of a bare `role`) is the discriminator,
// NOT the bare type === 'git'. The .has()-based ledger builders below are unchanged —
// only the Set handed to them is role-scoped here.
export const isGoodsBaseGitAccount = (a) => /^git_(\w+)$/.test(a?.account_role ?? a?.role ?? '');
export const buildGoodsBaseGitIds = (accounts) => new Set((accounts || []).filter(isGoodsBaseGitAccount).map(a => a.id));

// ── M10 finance-leg summary per operation (was GitTab finByOp — extracted verbatim) ──
export function buildFinByOp(financeData, gitAccountIds) {
  const m = {};
  for (const row of (financeData || [])) {
    const opId = row.operation_id;
    if (!opId) continue;
    if (row.tx_status && row.tx_status !== 'posted') continue;
    if (!m[opId]) {
      m[opId] = {
        invoice: 0, landed: 0, receivedGit: 0, gitBalance: 0, paid: 0, toBePaid: 0, landedCostPending: 0,
        // Legacy fields for payment filter / PaymentBadge
        status: null, subtype: null, payment_days: null, date: null,
      };
    }
    const amt = Number(row.amount) || 0;

    // INVOICE: Purchase, not landed_cost, not system. SF-114 Mode-2 supplier_credit does NOT
    // change the invoice (it posts to a contra-expense, not the goods) → excluded here.
    if (row.type === 'Purchase' && row.subtype !== 'landed_cost' && row.subtype !== 'supplier_credit' && !row.is_system) {
      m[opId].invoice += amt;
    }
    // LANDED: Purchase, subtype=landed_cost, not system
    if (row.type === 'Purchase' && row.subtype === 'landed_cost' && !row.is_system) {
      m[opId].landed += amt;
    }
    // RECEIVED GIT: any row where money leaves a GIT account
    if (row.account_from_id && gitAccountIds.has(row.account_from_id)) {
      m[opId].receivedGit += amt;
      m[opId].gitBalance  -= amt;
    }
    // GIT BALANCE += money flowing INTO GIT account
    if (row.account_to_id && gitAccountIds.has(row.account_to_id)) {
      m[opId].gitBalance += amt;
    }
    // PAID Pattern A: separate Payment row (credit purchase with supplier payment)
    if (row.type === 'Payment' && row.subtype === 'Supplier Payment') {
      m[opId].paid += amt;
    }
    // SF-114 Mode-2: a Supplier Credit behaves like a payment — it reduces what is owed.
    if (row.subtype === 'supplier_credit') {
      m[opId].paid += amt;
    }
    // PAID Pattern B: cash purchase (payment_days IS NULL, status=Paid, no separate Payment row)
    if (row.type === 'Purchase' && row.subtype !== 'landed_cost' && !row.is_system && row.status === 'Paid' && row.payment_days == null) {
      m[opId].paid += amt;
    }
    // PAID: cash landed costs paid same-day
    if (row.type === 'Purchase' && row.subtype === 'landed_cost' && !row.is_system && row.status === 'Paid') {
      m[opId].paid += amt;
    }
    // LANDED COSTS PENDING: separate tracking (M10 F6)
    if (row.type === 'Purchase' && row.subtype === 'landed_cost' && !row.is_system && row.status === 'Pending') {
      m[opId].landedCostPending += amt;
    }
    // TO BE PAID: all pending Purchase rows (goods + landed costs), not system.
    // SF-114 Mode-2 Supplier Credit (positive amount) REDUCES what is owed → subtract it.
    if (row.type === 'Purchase' && !row.is_system && row.status === 'Pending') {
      m[opId].toBePaid += (row.subtype === 'supplier_credit' ? -amt : amt);
    }

    // Legacy fields: status/subtype/date/payment_days (from first non-system Purchase rows)
    if (row.type === 'Purchase' && row.subtype !== 'landed_cost' && !row.is_system) {
      if (!m[opId].date || (row.date && row.date < m[opId].date)) m[opId].date = row.date;
      if (m[opId].payment_days == null && row.payment_days != null) m[opId].payment_days = row.payment_days;
      if (!m[opId].status) m[opId].status = row.status;
      if (row.status === 'Pending') m[opId].subtype = 'Credit';
      else if (!m[opId].subtype) m[opId].subtype = 'Cash';
    }
  }
  return m;
}

// ── SF110-B2.3: netted AP position per op (the cancellation-aware payment source) ──
// net owed to the supplier for an op = invoice − paid (the supplier-face GL delta,
// mirrors buildAccountLedger's Σ(CR)−Σ(DR)). B1's cancellation legs are Purchase rows
// posted with NEGATIVE amount from=Supplier→to=GIT (server.js:13614-13615): the
// 'cancellation' leg subtracts from invoice (nets AP to 0); 'refund_receivable' also
// subtracts from invoice while the original cash payment sits in `paid`, so
// invoice − paid goes NEGATIVE = a receivable. CRUX: net MUST be invoice − paid, not
// invoice alone and not toBePaid — a credit order paid via a SEPARATE type='Payment'
// row then cancelled+refunded has invoice netted to 0 AND paid>0, so only invoice−paid
// yields the negative that triggers 'Refund Due'. Feed this a buildFinByOp() entry
// (which captures Pattern-A separate Payment rows via `paid`); do NOT hand-roll from a
// Purchase-only loop (it structurally misses the separate Payment row).
export function netApOf(finEntry) {
  if (!finEntry) return 0;
  return (Number(finEntry.invoice) || 0) - (Number(finEntry.paid) || 0);
}

// SF110-B2.3: net AP from a FLAT finance-row array that already belongs to one op
// (OperationDetailView's ledger.finance_rows, which carry NO operation_id so buildFinByOp
// would skip them). Reproduces buildFinByOp's invoice/paid accumulation, invoice − paid.
export function netApForRows(rows) {
  let invoice = 0, paid = 0;
  for (const row of (rows || [])) {
    if (row.tx_status && row.tx_status !== 'posted') continue;
    const amt = Number(row.amount) || 0;
    if (row.type === 'Purchase' && row.subtype !== 'landed_cost' && row.subtype !== 'supplier_credit' && !row.is_system) invoice += amt;
    if (row.type === 'Payment' && row.subtype === 'Supplier Payment') paid += amt;
    if (row.subtype === 'supplier_credit') paid += amt;
    if (row.type === 'Purchase' && row.subtype !== 'landed_cost' && !row.is_system && row.status === 'Paid' && row.payment_days == null) paid += amt;
    if (row.type === 'Purchase' && row.subtype === 'landed_cost' && !row.is_system && row.status === 'Paid') paid += amt;
  }
  return invoice - paid;
}

// SF110-B2.3: given a net-AP figure + whether the order is cancelled, resolve the
// cancellation-aware payment STATE. Non-cancelled → null (caller keeps the existing
// byte-identical Paid/Pending/Overdue path). Tolerance guards float noise.
export function cancelledPayState(netAP, isCancelled, hasRefundDisposition = false) {
  // SF-111 EDIT (paid-floor): a below-paid qty/price EDIT posts a refund_receivable leg but,
  // by the offset-leg model, leaves lines en_route (a price-down never cancels a line; a
  // partial-line qty-down stays 'partial'). So the order does NOT roll up to cancelled and
  // isCancelled stays false — yet net-AP is legitimately negative and the supplier owes a
  // refund. Widen 'refund-due' to also fire when an actual refund_receivable disposition is
  // present (hasRefundDisposition), NOT on any negative net (which could be negative for
  // unrelated reasons). Scoped B2.3 extension — see hasRefundDisposition().
  if (netAP < -0.01 && (isCancelled || hasRefundDisposition)) return 'refund-due';
  if (!isCancelled) return null;           // non-cancelled + no refund leg → old path unchanged
  if (Math.abs(netAP) <= 0.01) return 'settled'; // obligation closed (reversed to 0)
  return null;                             // net > 0 (surviving debt) → old path (Pending/Overdue)
}

// SF-111 EDIT: does this op carry a posted refund_receivable disposition leg? Used to let the
// 'Refund Due' pill fire on a below-paid EDIT that did not cancel any line (see cancelledPayState).
export function hasRefundDisposition(rows) {
  return (rows || []).some(r =>
    r.subtype === 'refund_receivable' && (r.tx_status === 'posted' || !r.tx_status));
}

// ── SF110-B2.4: the REMARKS FEED — a curated READ over correction_log ─────────────
// A pure view: correction_log rows (what B1 already writes) → business-language strings.
// NO writes, no table, no schema. Only the "noticeable event" change_types map to a
// remark; base-state transitions (safe_field_edit, reverse/delete_group action rows,
// null/unmapped change_types) return null and are NEVER counted or shown.
//
// F2 STAYS CUT (SF110-F2-RELIABILITY-MARKERS-SKIPPED): no eta_change_reason/party who-why,
// no 'Reduced' scorecard — line_reduction is a plain remark string only.
//
// Formatting is INJECTED (fmtDate/fmtNumber) so this module stays dependency-free and the
// mapping is unit-testable without the formatting cache. Callers pass utils/format helpers.

const _identity = (v) => (v == null ? '' : String(v));

// remarkForCorrection(row, { fmtDate, fmtNumber }) → business-language string | null.
// Rows carry: change_type, field, old_value, new_value (String()'d by writeCorrectionLog),
// was_late_at_change. old/new for qty are stringified numbers; for eta they are ISO dates;
// for disposal legs new_value is "subtype:amount" (we ignore the amount — the change_type
// already names the event).
export function remarkForCorrection(row, opts = {}) {
  if (!row) return null;
  const fmtDate   = opts.fmtDate   || _identity;
  const fmtNumber = opts.fmtNumber || _identity;
  const ct = row.change_type;
  switch (ct) {
    case 'line_reduction': {
      const from = fmtNumber(Number(row.old_value));
      const to   = fmtNumber(Number(row.new_value));
      return `Qty reduced ${from}→${to}`;
    }
    case 'price_reduction': {
      // SF-111 EDIT: a PRICE cut (distinct from line_reduction's QTY wording). The switch
      // must have its own case or a pure price edit would misrender as 'Qty reduced'.
      const from = fmtNumber(Number(row.old_value));
      const to   = fmtNumber(Number(row.new_value));
      return `Price reduced ${from}→${to}`;
    }
    case 'credit_note_price': {
      // Credit note applied directly on price (owner Mode-1): the order received a supplier credit
      // that reduced the line price — distinct wording from a plain price edit so the history reads
      // as a CREDIT, not a correction.
      const from = fmtNumber(Number(row.old_value));
      const to   = fmtNumber(Number(row.new_value));
      return `Credit note applied on price ${from}→${to}`;
    }
    case 'line_abandon':
      return 'Line abandoned (qty→0)';
    case 'cancellation':
      return 'Cancelled';
    case 'eta_change': {
      const from = fmtDate(row.old_value);
      const to   = fmtDate(row.new_value);
      const base = `ETA pushed ${from}→${to}`;
      return row.was_late_at_change === true ? `${base} (was late)` : base;
    }
    case 'refund_receivable':
      return 'Refunded';
    case 'writeoff':
      return 'Written off';
    default:
      // safe_field_edit, null, reverse/delete_group action-only rows → NOT a remark.
      return null;
  }
}

// buildRemarksLog(rows, opts) → [{ at, remark, change_type, line_id }] for the ORDER PAGE.
// OLDEST-FIRST (chronological): reads top-to-bottom as the order's lifecycle story.
// Dedups identical remark strings within the same line_id — one logical cancel writes
// multiple correction_log rows (e.g. the receipt_status flip AND the disposal leg both log
// change_type='cancellation'), which would otherwise show "Cancelled" twice. Genuinely
// distinct events (a second line_reduction, a different line) survive.
export function buildRemarksLog(rows, opts = {}) {
  const mapped = [];
  const seen = new Set();
  for (const row of (rows || [])) {
    const remark = remarkForCorrection(row, opts);
    if (!remark) continue;
    const dedupKey = `${row.line_id || ''}|${remark}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);
    mapped.push({ at: row.at, remark, change_type: row.change_type, line_id: row.line_id || null });
  }
  // Oldest-first. writeCorrectionLog stamps `at` (timestamptz default now()); ties keep
  // insertion order (stable sort) which mirrors the write sequence.
  mapped.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  return mapped;
}

// countRemarks(rows, opts) → the badge count = number of DISTINCT noticeable events.
// 421: aligned to OrderTabs' History tab-HEADER `counted` Set so the tab-label badge + the
// orders-list badge AGREE with the tab-header count on every order. buildRemarksLog dedups on
// (line_id | remark) for the DISPLAYED remarks LIST — that row output is left UNCHANGED. The
// COUNT, however, is computed here with OrderTabs' key (per 420): (line_id | target_id | remark),
// so a same-line re-cancel that posts a NEW finance disposal leg (a new target_id) counts
// DISTINCTLY — previously it over-merged under (line_id | remark). Key expression matches
// OrderTabs.jsx verbatim so the two functions produce identical counts. The L01-same-target_id
// case (the two 400→300 reductions share one target_id) still MERGES identically in both
// functions (target_id alone doesn't split them — the timestamp-split grouping is v0.39), so the
// two surfaces stay consistent. Count-only: buildRemarksLog's rows are not touched.
export function countRemarks(rows, opts = {}) {
  const seen = new Set();
  for (const row of (rows || [])) {
    const remark = remarkForCorrection(row, opts);
    if (!remark) continue;
    const key = `${row.line_id || ''}|${row.target_id || row.landed_cost_id || ''}|${remark}`;
    if (seen.has(key)) continue;
    seen.add(key);
  }
  return seen.size;
}

// ── GES-26: SELL-SIDE remarks ───────────────────────────────────────────────────
// Sales ops carry NONE of the buy-side change_types, so remarkForCorrection would always
// return null → an empty column. These map the AUDITED sell-side signal instead: the four
// sell change_types + the GES-24 Part-1 null-change_type safe-field edits. Same injected-fmt
// contract as remarkForCorrection; buildSaleRemarksLog/countSaleRemarks mirror the buy-side pair.
export function remarkForSaleCorrection(row, opts = {}) {
  if (!row) return null;
  const fmtDate = opts.fmtDate || _identity;
  switch (row.change_type) {
    case 'sale_reclassify':        return 'Reclassified';
    case 'revenue_reallocate':
    case 'revenue_leg_reallocate': return 'Revenue re-allocated';
    case 'cancel':                 return 'Cancelled';
    case null:
    case undefined:
      // GES-24 Part-1 safe-field edits (change_type null, field names the edited column).
      switch (row.field) {
        case 'date':       return `Re-dated ${fmtDate(row.old_value)}→${fmtDate(row.new_value)}`;
        case 'partner_id': return 'Customer changed';
        case 'status':     return 'Method changed';
        case 'notes':      return 'Note updated';
        default:           return null;
      }
    default:
      return null;
  }
}

// buildSaleRemarksLog(rows, opts) → [{ at, remark, change_type, line_id }], oldest-first. Mirrors
// buildRemarksLog: dedups identical remark strings within the same line_id.
export function buildSaleRemarksLog(rows, opts = {}) {
  const mapped = [];
  const seen = new Set();
  for (const row of (rows || [])) {
    const remark = remarkForSaleCorrection(row, opts);
    if (!remark) continue;
    const dedupKey = `${row.line_id || ''}|${remark}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);
    mapped.push({ at: row.at, remark, change_type: row.change_type, line_id: row.line_id || null });
  }
  mapped.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
  return mapped;
}

// countSaleRemarks(rows, opts) → badge count. Mirrors countRemarks' (line_id | target_id | remark) key.
export function countSaleRemarks(rows, opts = {}) {
  const seen = new Set();
  for (const row of (rows || [])) {
    const remark = remarkForSaleCorrection(row, opts);
    if (!remark) continue;
    const key = `${row.line_id || ''}|${row.target_id || ''}|${remark}`;
    if (seen.has(key)) continue;
    seen.add(key);
  }
  return seen.size;
}

// GIT balance per operation only (M10) — was SuppliersTab gitBalanceByOp / OverviewTab
// finByOpGit.gitBalance. Same arithmetic as buildFinByOp().gitBalance.
export function buildGitBalanceByOp(financeData, gitAccountIds) {
  const m = {};
  for (const row of (financeData || [])) {
    const opId = row.operation_id;
    if (!opId) continue;
    if (row.tx_status && row.tx_status !== 'posted') continue;
    if (row.account_to_id && gitAccountIds.has(row.account_to_id))     m[opId] = (m[opId] || 0) + (Number(row.amount) || 0);
    if (row.account_from_id && gitAccountIds.has(row.account_from_id)) m[opId] = (m[opId] || 0) - (Number(row.amount) || 0);
  }
  return m;
}

// ── account → sole-partner 1:1 recovery map (partner_id resolution helper) ──────────
// For each partner-bearing account of `accountType`, recover the single distinct non-null partner_id
// across ALL its posted finance legs (Purchase/Sale legs carry the real partner even when a settlement
// leg is NULL-partner). Absent when 0 or >1 distinct — a SHARED "Various" account stays
// UNRESOLVABLE. This is the SAME identity PaymentManagementPage / E11 / computeAged resolve through
// (ID-based, never name-based), so the payable and receivable ledgers attribute a leg identically.
export function buildAcct1to1(financeData, accounts, accountType = 'supplier') {
  const typeById = new Map((accounts || []).map(a => [a.id, (a.type || '').toLowerCase()]));
  const sets = {};
  for (const r of (financeData || [])) {
    if (!r.partner_id) continue;
    for (const aid of [r.account_to_id, r.account_from_id]) {
      if (aid && typeById.get(aid) === accountType) (sets[aid] ||= new Set()).add(r.partner_id);
    }
  }
  const m = {};
  for (const [aid, s] of Object.entries(sets)) if (s.size === 1) m[aid] = [...s][0];
  return m;
}

// Resolve the partner a leg belongs to on a given supplier ACCOUNT: its stamped partner_id, else the
// 1:1-account recovery for that account (a NULL-partner leg on a DEDICATED account), else null (a
// NULL-partner leg on a SHARED account). The ONE identity E11/computeAged/the supplier ledgers resolve
// through — centralised so the parity is structural, not hand-copied `?? acct1to1[...]` sites.
export function resolvePartnerId(row, accountId, acct1to1) {
  return row.partner_id ?? (acct1to1 ? acct1to1[accountId] : null) ?? null;
}

// ── GL account ledger (supplier face) ────────────────────────────────────────────
// The supplier ledger IS the GL account ledger: every posted row touching the account,
// chronological with a stable _seq tiebreak, running balance = Σ(CR) − Σ(DR) so the
// FINAL row == balance(account) == the tile == the BS line. Credit subtypes render as
// negative Invoiced entries (M10 display rule: a credit is never a cash payment).
//
// partnerId (optional) enables PARTNER-RESOLVED attribution (E11/computeAged parity): a leg is kept
// iff (row.partner_id ?? acct1to1[accountId] ?? null) === partnerId — i.e. stamped legs go to their
// partner, NULL-partner legs on a DEDICATED (1:1) account are recovered to that account's sole
// partner, and NULL-partner legs on a SHARED account (acct1to1 absent) resolve to null → excluded
// from any specific partner. When partnerId is left undefined the account is walked whole (no
// filtering) — the account-total mode existing callers (HistoryTab) rely on, byte-identical.
// `receivable` (794 direction inversion, computeAged parity): the buy side settles a PAYABLE with a
// CREDIT (increaseSide = account_from, settleSide = account_to); the sell side settles a RECEIVABLE
// with a DEBIT (increaseSide = account_to, settleSide = account_from) — the SAME two side variables
// computeAged uses. Default false = the supplier face, byte-identical to before.
export function buildAccountLedger(financeData, accountId, { partnerName = null, partnerId, acct1to1 = null, receivable = false } = {}) {
  const increaseSide = receivable ? 'account_to_id'   : 'account_from_id';   // side that RAISES the balance owed
  const settleSide   = receivable ? 'account_from_id' : 'account_to_id';     // side that SETTLES it
  const raw = [];
  for (const row of (financeData || [])) {
    if (row.tx_status && row.tx_status !== 'posted') continue;
    const isInc = row[increaseSide] === accountId; // increases what is owed (payable CR / receivable DR)
    const isSet = row[settleSide]   === accountId; // settles it
    if (!isInc && !isSet) continue;
    if (partnerId !== undefined && resolvePartnerId(row, accountId, acct1to1) !== partnerId) continue;
    const amt = Number(row.amount) || 0;
    const isCreditSubtype = row.subtype === 'credit_note' || row.subtype === 'supplier_credit';
    // GES-16: a recognised fixed discount (leg to 'Sales discounts', subtype sales_discount) reduces the
    // receivable but is NOT money received — its own `discount` column, never `paid`. Balance unchanged.
    const isDiscount = receivable && row.subtype === 'sales_discount';
    let invoiced = 0, paid = 0, discount = 0;
    if (isDiscount) {
      discount = isSet ? amt : -amt;
    } else if (isCreditSubtype) {
      // credit note: negative on the increase side → negative invoiced; a credit on the settle side → negative invoiced
      invoiced = isInc ? amt : -amt;
    } else if (isInc) {
      invoiced = amt;
    } else {
      paid = amt;
    }
    const label =
      row.type === 'Opening' ? 'Opening balance'
      : row.subtype === 'supplier_credit' ? 'Supplier Credit'
      : row.subtype === 'credit_note' ? 'Credit Note'
      : row.subtype === 'landed_cost' ? 'Landed cost'
      : isDiscount ? 'Discount'
      : row.type === 'Payment' ? (row.subtype === 'Supplier Payment' ? 'Supplier Payment' : `Payment${row.subtype ? ' · ' + row.subtype : ''}`)
      : row.type;
    raw.push({
      _date: row.date || '',
      _isPmt: !isCreditSubtype && isSet,
      _isCredit: isCreditSubtype,
      _isLanded: row.subtype === 'landed_cost',
      _isDiscount: isDiscount,
      date: row.date,
      description: `${label} · ${row.operation_id || '—'}`,
      operation_id: row.operation_id || null,
      reference_id: row.reference_id || null,   // W13 (E9): the optional free-text reference, shown by the hidden Reference column
      partner_name: partnerName,
      type: row.type, subtype: row.subtype, status: row.status,
      invoiced, paid, discount,
      balanceDelta: invoiced - paid - discount,
      balance: 0,
    });
  }
  // Chronological, Payments after Purchases on the same date, stable within groups.
  raw.sort((a, b) => (a._date.localeCompare(b._date)) || ((a._isPmt ? 1 : 0) - (b._isPmt ? 1 : 0)));
  let bal = 0;
  raw.forEach((r, i) => { bal += r.balanceDelta; r.balance = bal; r._seq = i; });
  return raw;
}

// ── Partner-keyed supplier ledger (all activity, running outstanding) ──────────────
// Turns the AP-account ledger into a PARTNER ledger that shows EVERY purchase (cash or
// credit, any receipt status) and EVERY payment, with a running `outstanding` balance.
//   • CREDIT purchases + payments + opening + credit notes → reuse buildAccountLedger for
//     the partner's supplier ACCOUNT(s) (DRY; those rows reconcile to balance(account)).
//   • CASH purchases (Purchase rows whose account_from resolves to a NON-supplier
//     cash/bank account, keyed by partner_id) never touch a supplier account, so they are
//     disjoint from the account walk. Each emits TWO same-date rows — a +purchase then an
//     immediate −payment — that net to 0, so the FINAL balance is unchanged (== AP) and the
//     BS tie is preserved. A credit order's purchase stays outstanding until its payment.
// Output rows are a SUPERSET of buildAccountLedger's shape so the existing RunningLedgerTable
// / CSV render unchanged: {date,_date,description,operation_id,type,subtype,status,invoiced,
//  paid,balance,balanceDelta,_isPmt,_isCredit,_isLanded,partner_name,_seq}.
// Attribution is by RESOLVED PARTNER (partner_id ?? 1:1-account recovery), the SAME identity
// E11/computeAged use — NOT by a pre-mapped account list (which mis-attributed: it walked a partner's
// SHARED "Various" account whole, summing OTHER partners' legs, while its dedicated NULL-partner
// account was orphaned). So every SUPPLIER account is walked and buildAccountLedger's partner filter
// keeps only this partner's legs → Net(partner) == E11 net(partner) by construction. `partnerId` may
// be a real partner id, or null (the unattributed bucket: NULL-partner legs on a shared account +
// NULL-partner cash purchases).
export function buildSupplierLedger(financeData, accounts, { partnerId = null, partnerName = null, acct1to1 = null } = {}) {
  const a2p = acct1to1 || buildAcct1to1(financeData, accounts);
  const typeById = new Map((accounts || []).map(a => [a.id, (a.type || '').toLowerCase()]));
  const supplierAcctIds = [...typeById.entries()].filter(([, t]) => t === 'supplier').map(([id]) => id);
  const acctIdSet = new Set(supplierAcctIds);

  const raw = [];
  // 1) ACCOUNT-WALK rows (credit purchases, payments, opening, credit notes) — partner-resolved
  //    across EVERY supplier account (buildAccountLedger keeps only this partner's legs).
  for (const acctId of supplierAcctIds) {
    for (const r of buildAccountLedger(financeData, acctId, { partnerName, partnerId, acct1to1: a2p })) {
      // strip the per-account running balance; a merged running balance is recomputed below
      raw.push({ ...r, balance: 0, _seq: undefined });
    }
  }

  // 2) CASH-PURCHASE rows: Purchase whose account_from is a resolved NON-supplier account
  //    (cash/bank) and belongs to this partner. Emit +purchase then −payment (same date).
  let pair = 0;
  for (const row of (financeData || [])) {
    if (row.type !== 'Purchase') continue;
    if (row.tx_status && row.tx_status !== 'posted') continue;
    if ((row.partner_id ?? null) !== (partnerId ?? null)) continue;
    // audit 156: unresolved account (undefined) must still SKIP (don't guess); paymentMethodOf
    // would default it to 'Cash'. Keep the undefined guard, then use the shared helper for the
    // resolved supplier-vs-cash decision so this mirrors the badge classifier byte-for-byte.
    const fromType = typeById.get(row.account_from_id);
    if (fromType === undefined) continue;      // unresolved account → skip (don't guess)
    if (paymentMethodOf(row, typeById) === 'Credit') continue; // credit purchase → in the account walk
    // Never double-count a row already in the account walk. A supplier_credit is type==='Purchase'
    // with a NON-supplier account_from (contra) but settles via account_to = the supplier account
    // → it's in the walk (rendered "Supplier Credit"); guarding account_to too skips it here.
    if (acctIdSet.has(row.account_from_id) || acctIdSet.has(row.account_to_id)) continue;
    const amt = Number(row.amount) || 0;
    const opId = row.operation_id || null;
    const _p = pair++;
    raw.push({
      _date: row.date || '', _isPmt: false, _isCredit: false, _isLanded: false,
      _pair: _p, _leg: 0,
      date: row.date, description: `Purchase · ${opId || '—'}`, operation_id: opId, reference_id: row.reference_id || null,
      partner_name: partnerName, type: 'Purchase', subtype: row.subtype || null, status: row.status,
      invoiced: amt, paid: 0, balanceDelta: amt, balance: 0,
    });
    raw.push({
      _date: row.date || '', _isPmt: true, _isCredit: false, _isLanded: false,
      _pair: _p, _leg: 1,
      date: row.date, description: `Cash payment · ${opId || '—'}`, operation_id: opId, reference_id: row.reference_id || null,
      partner_name: partnerName, type: 'Payment', subtype: row.subtype || null, status: row.status,
      invoiced: 0, paid: amt, balanceDelta: -amt, balance: 0,
    });
  }

  // 3) Merge + chronological running balance. Payments after purchases same-date; a cash
  //    pair stays adjacent (shared _pair, leg 0 before 1). Stable within groups.
  raw.sort((a, b) =>
    (a._date.localeCompare(b._date))
    || ((a._isPmt ? 1 : 0) - (b._isPmt ? 1 : 0))
    || ((a._pair ?? -1) - (b._pair ?? -1))
    || ((a._leg ?? 0) - (b._leg ?? 0)));
  let bal = 0;
  raw.forEach((r, i) => { bal += r.balanceDelta; r.balance = bal; r._seq = i; });
  return raw;
}

// ── Partner-keyed CUSTOMER ledger (receivable twin of buildSupplierLedger) ─────────
// The receivable-side mirror of buildSupplierLedger: same running-balance derivation from the SAME
// posted finance rows, attributed by RESOLVED partner (partner_id ?? 1:1 client-account recovery),
// but in the RECEIVABLE direction — a customer invoice (credit SALE landing in the client account)
// RAISES what they owe, a received payment/credit LOWERS it (the sign mirror of the payable ledger,
// per the 794 increaseSide/settleSide inversion). Positive running balance = the customer owes us.
//   • CREDIT sales + receipts + opening + credit notes → reuse buildAccountLedger({ receivable:true })
//     over EVERY client account (partner-resolved: keeps only this customer's legs, so
//     Net(customer) == E11 receivable net(customer) by construction).
//   • CASH sales (Sale rows that touch NO client account — money received on the spot) never land in
//     a client account, so they are disjoint from the walk. Each emits TWO same-date rows — a
//     +invoice then an immediate −receipt — that net to 0, so the FINAL balance is unchanged (== AR).
// Output rows are the SAME shape buildSupplierLedger emits, so RunningLedgerTable renders unchanged.
export function buildCustomerLedger(financeData, accounts, { partnerId = null, partnerName = null, acct1to1 = null } = {}) {
  const a2p = acct1to1 || buildAcct1to1(financeData, accounts, 'client');
  const typeById = new Map((accounts || []).map(a => [a.id, (a.type || '').toLowerCase()]));
  const clientAcctIds = [...typeById.entries()].filter(([, t]) => t === 'client').map(([id]) => id);
  const acctIdSet = new Set(clientAcctIds);

  const raw = [];
  // 1) ACCOUNT-WALK rows (credit sales, receipts, opening, credit notes) — RECEIVABLE direction,
  //    partner-resolved across EVERY client account (buildAccountLedger keeps only this partner's legs).
  for (const acctId of clientAcctIds) {
    for (const r of buildAccountLedger(financeData, acctId, { partnerName, partnerId, acct1to1: a2p, receivable: true })) {
      raw.push({ ...r, balance: 0, _seq: undefined });
    }
  }

  // 2) CASH-SALE rows: a Sale that touches NO client account (received on the spot), keyed by
  //    partner_id. Emit +invoice then −receipt (same date). A credit sale DOES touch a client account
  //    (account_to = client) → it is already in the walk, so it is guarded out here (no double-count).
  //    GES-16: a till-channel discount (sales_discount leg from the till, no client account) gives part of
  //    the takings back: the receipt shows the CASH kept and the discount its own row (pair still nets 0).
  const tillDiscount = new Map();   // operation_id -> discount not yet matched to a cash-sale receipt
  for (const row of (financeData || [])) {
    if (row.subtype !== 'sales_discount' || (row.tx_status && row.tx_status !== 'posted')) continue;
    if ((row.partner_id ?? null) !== (partnerId ?? null)) continue;
    if (acctIdSet.has(row.account_from_id) || acctIdSet.has(row.account_to_id)) continue;   // client channel → in the walk
    tillDiscount.set(row.operation_id, (tillDiscount.get(row.operation_id) || 0) + (Number(row.amount) || 0));
  }
  let pair = 0;
  for (const row of (financeData || [])) {
    if (row.type !== 'Sale') continue;
    if (row.tx_status && row.tx_status !== 'posted') continue;
    if ((row.partner_id ?? null) !== (partnerId ?? null)) continue;
    if (acctIdSet.has(row.account_from_id) || acctIdSet.has(row.account_to_id)) continue; // credit sale → in the walk
    const amt = Number(row.amount) || 0;
    const opId = row.operation_id || null;
    const _p = pair++;
    raw.push({
      _date: row.date || '', _isPmt: false, _isCredit: false, _isLanded: false,
      _pair: _p, _leg: 0,
      date: row.date, description: `Sale · ${opId || '—'}`, operation_id: opId, reference_id: row.reference_id || null,
      partner_name: partnerName, type: 'Sale', subtype: row.subtype || null, status: row.status,
      invoiced: amt, paid: 0, balanceDelta: amt, balance: 0,
    });
    // ponytail: matched per operation, oldest Sale leg first (finance order); a till discount with no cash
    // Sale leg of the same op for this partner is not shown — add per-event matching if that ever occurs.
    const disc = Math.min(tillDiscount.get(opId) || 0, amt);
    if (disc > 0) tillDiscount.set(opId, (tillDiscount.get(opId) || 0) - disc);
    raw.push({
      _date: row.date || '', _isPmt: true, _isCredit: false, _isLanded: false,
      _pair: _p, _leg: 1,
      date: row.date, description: `Cash receipt · ${opId || '—'}`, operation_id: opId, reference_id: row.reference_id || null,
      partner_name: partnerName, type: 'Payment', subtype: row.subtype || null, status: row.status,
      invoiced: 0, paid: amt - disc, discount: 0, balanceDelta: -(amt - disc), balance: 0,
    });
    if (disc > 0) raw.push({
      _date: row.date || '', _isPmt: true, _isCredit: false, _isLanded: false, _isDiscount: true,
      _pair: _p, _leg: 2,
      date: row.date, description: `Discount · ${opId || '—'}`, operation_id: opId, reference_id: row.reference_id || null,
      partner_name: partnerName, type: 'Expense', subtype: 'sales_discount', status: row.status,
      invoiced: 0, paid: 0, discount: disc, balanceDelta: -disc, balance: 0,
    });
  }

  // 3) Merge + chronological running balance (same rule as the supplier ledger): receipts after
  //    invoices same-date; a cash pair stays adjacent (shared _pair, leg 0 before 1).
  raw.sort((a, b) =>
    (a._date.localeCompare(b._date))
    || ((a._isPmt ? 1 : 0) - (b._isPmt ? 1 : 0))
    || ((a._pair ?? -1) - (b._pair ?? -1))
    || ((a._leg ?? 0) - (b._leg ?? 0)));
  let bal = 0;
  raw.forEach((r, i) => { bal += r.balanceDelta; r.balance = bal; r._seq = i; });
  return raw;
}

// Footer for a GL account ledger: net == balance(account) by construction.
export function ledgerFooterOf(rows) {
  let totalInvoiced = 0, totalPaid = 0, totalDiscount = 0;
  for (const r of rows) { totalInvoiced += r.invoiced; totalPaid += r.paid; totalDiscount += r.discount || 0; }
  return { totalInvoiced, totalPaid, totalDiscount, net: totalInvoiced - totalPaid - totalDiscount };   // GES-16: discount is not received
}

// ── Open-item helpers (the aged-payables GL row set, shared by cards/buckets) ──────
// items = the open items returned by /reports/aged-payables (residuals after FIFO
// settlement on the GL account rows). due_date == null ⇒ no terms (cash/opening):
// per M10 those are never "overdue" — they sit in Current.
export function bucketOpenItems(items, todayStr, dueSoonDays = 7) {
  const cutSoon = new Date(new Date(todayStr).getTime() + dueSoonDays * 86400000).toISOString().slice(0, 10);
  let overdue = 0, dueSoon = 0, current = 0, dueSoonMinDays = null;
  for (const it of (items || [])) {
    const amt = Number(it.amount) || 0;
    if (it.due_date && it.due_date < todayStr) overdue += amt;
    else if (it.due_date && it.due_date <= cutSoon) {
      dueSoon += amt;
      const diff = Math.round((new Date(it.due_date) - new Date(todayStr)) / 86400000);
      if (dueSoonMinDays === null || diff < dueSoonMinDays) dueSoonMinDays = diff;
    } else current += amt;
  }
  return { overdue, dueSoon, current, dueSoonMinDays, total: overdue + dueSoon + current };
}

export function allOpenItems(payablesData) {
  const out = [];
  for (const r of (payablesData || [])) for (const it of (r.items || [])) out.push(it);
  return out;
}

// ── 769: THE PAYABLES IDENTITY ─────────────────────────────────────────────────────
//   owed on orders + order-less obligations = GROSS      (obligations, BEFORE credit)
//   GROSS − on-account credit pool          = NET        (= E11 Σ payable account_balance = the GL)
// NOT a new computation. ReconciliationTab.jsx already computes exactly this per supplier
// (`grossPayable = owedTotal + orderLessTotal` :165, less the pool → E11 net, `bridgeResidual` 0.00
// :211). It is hoisted here so the headlines everyone reads state the SAME identity off the SAME
// rows — the aged scopes computeAged certifies against the account balance (Σ items == balance).
//
// An ORDER-LESS obligation is a bare AP increase with NO purchase order behind it: a transfer/advance
// leg, or a credit expense booked straight onto the payable. It carries an operation_id of its OWN —
// which is exactly why filtering aged items on `it.operation_id` never meant "orders only". 768
// measured what that false assumption hid: 180 700 of order-less obligations sitting inside a figure
// labelled as order payables (811 750 + 180 700 = 992 450 gross; − 457 816.22 pool = 534 633.78 = GL).
//
// 794 — CLASS PARAMETER (A1). The identity is sign-symmetric and computeAged is class-parameterised,
// so the ONLY thing that is not two-sided here is the order-bearing obligation TYPE: a purchase order's
// AP increase is `type='Purchase'`, a sales order's AR increase is `type='Sale'`. Without the parameter
// every customer invoice fell through to "order-less" and `payablesIdentity` reported orders = 0 on the
// receivable side. `cls` DEFAULTS to 'payable', and the payable arm of the ternary is the literal that
// was here before — so every existing 1-argument caller is byte-identical (proved by the differential
// harness in 794's RESULT: 4 096-case matrix + the live aged-payables payload, 0 diffs).
// 883 — A SALE FEE IS ORDER-BOUND, NEVER ORDER-LESS. This block's own definition of order-less is
// "money owed with NO sales order behind it" — and a fee leg HAS one: it carries the parent sale's
// operation_id, and 880 already folded its customer receivable INTO v_sales_order. Keyed on `type`
// alone the same receivable was surfaced TWICE (once in the order, once here), and the Payments
// page's Δ banner fired with Δ = exactly the fee total (1 549 = 225 + 1 324 observed live).
// `type` cannot make the cut: since 872 retyped fee legs off 'Sale', the live fee legs carry the
// SAME type ('Other Revenue') and the SAME buckets (manure_sale, eggcrate_scrap_sale) as the
// genuine order-less lines — `sales_fee_id` is the ONLY field that separates the two populations.
// Excluding on it makes this predicate and v_sales_order agree by the SAME rule rather than by
// coincidence. Genuine manual Other-Revenue-to-customer legs (sales_fee_id NULL) stay order-less.
// 906 — THE FEE EXEMPTION IS RECEIVABLE-ONLY, mirroring server.js partitionOf conjunct for conjunct.
// The exemption above exists because v_sales_order folds the fee into its order — a SELL-side fact. No
// buy-side fold exists (a sale fee's vendor leg carries the SALES operation_id, which procurement's order
// list can never contain), so on the payable side it suppressed a double-count that cannot occur and left
// this predicate disagreeing with set membership. Changed in LOCKSTEP with the server: these two are
// required to stay identical, and letting them drift is the very defect A15 exists to catch.
// 948 (N7) — SINGLE-HOMED. This used to RE-DERIVE the partition here, conjunct-for-conjunct with the
// server, with both sides carrying "keep these identical" comments and A15 watching for the drift. The
// server already publishes the answer: computeAged STAMPS every item it emits with
// `partition` ('pool' | 'order' | 'order_less'), and its own comment says "every reader READS the flag
// instead of deriving the split a second time" — the web was the last reader still deriving.
// Proven safe before collapsing (948): over all 65 live open items across BOTH classes, the old
// derivation and the stamp agreed on EVERY row — 0 disagreements, compared by row, not by totals.
// 🔴 DRIFT IS NOW STRUCTURALLY IMPOSSIBLE, not merely watched: there is one derivation. `cls` is kept in
// the signature so the five call sites are untouched, but it is no longer read — the server stamps per
// class already, which is why passing the wrong `cls` used to be able to mis-route money and now cannot.
// The stamp is always present: every consumer reads /reports/aged-payables|aged-receivables, which stamp
// it. If it is ever absent that is a payload bug, and this warns rather than silently answering `false`
// (a silent false would route an order-less line into `orders` — money in the wrong term).
export const isOrderLessItem = (it, _cls = 'payable') => {
  if (it && it.partition == null && typeof console !== 'undefined')
    console.warn('isOrderLessItem: item has no `partition` stamp — expected computeAged to stamp it', it);
  return it?.partition === 'order_less';
};

// Σ the identity's terms off the aged-payables / aged-receivables payload, optionally scoped to one
// partner. `net` is SIGNED by design: it goes NEGATIVE when a partner is over-credited (they owe you).
// Never clamp it at 0 — a clamp both hides that position and makes the all-suppliers net disagree with
// Σ of the per-supplier nets (768 OBSERVED that self-contradiction live: 353 933.78 vs 357 970.14).
//
// 794 — ⚠ THE HARD RULE. This returns ONE class's position. A payable and a receivable are
// OPPOSITE-SIGNED claims; never sum the two into a single headline. Call it once per class and print
// the two figures separately.
// 47002 - the RECEIVABLE identity reads the API's customer position snapshot (net, gross, credit); it derives none of them. `pool` is the allocatable credit ONLY (credit_on_account, == the callout); advances held and refunds held are separate terms (advances, refundsHeld) so gross - pool - advances - refundsHeld = net. Only the order / order-less split of the gross is itemised here, off the items the API flags.
function receivableIdentity(rows, partnerId) {
  const p = receivablePosition(rows, partnerId);
  let orderLess = 0;
  for (const r of (rows || [])) {
    if (partnerId && r.partner_id !== partnerId) continue;
    for (const it of (r.items || [])) {
      const amt = Number(it.open_amount ?? it.amount) || 0;
      if (it.operation_id && amt > 0 && !r.account_role && isOrderLessItem(it, 'receivable')) orderLess += amt;
    }
  }
  const round = (n) => Math.round(n * 100) / 100;
  return { orders: round(p.gross - orderLess), orderLess: round(orderLess), gross: p.gross, pool: p.creditOnAccount, net: p.net,
    creditOnAccount: p.creditOnAccount, advances: p.advances, refunds: p.refunds, refundsHeld: p.refundsHeld };
}
export function payablesIdentity(payablesData, partnerId = null, cls = 'payable') {
  if (cls === 'receivable') return receivableIdentity(payablesData, partnerId);
  let orders = 0, orderLess = 0, pool = 0;
  for (const r of (payablesData || [])) {
    if (partnerId && r.partner_id !== partnerId) continue;
    for (const it of (r.items || [])) {
      const amt = Number(it.amount) || 0;
      if (!it.operation_id) { pool -= amt; continue; }   // pool items are negative residuals
      if (isOrderLessItem(it, cls)) orderLess += amt; else orders += amt;
    }
  }
  const r2 = (n) => Math.round(n * 100) / 100;
  return { orders: r2(orders), orderLess: r2(orderLess), gross: r2(orders + orderLess),
    pool: r2(pool), net: r2(orders + orderLess - pool) };
}

// ── M10 SPEND / GROSS (124, MA18) ─────────────────────────────────────────────────
// ONE calculator for every procurement spend/gross number (MoP Addition 4). The scope
// key spans 6 display grains (KPI / by-category / by-supplier / heatmap / monthly-trend /
// grand-total) off ONE key, so this is a shared FE helper over the already-fetched
// purchases + financeData, not a backend endpoint.
//
//   computeSpend(purchases, financeData, { op, supplier, category, periodFrom, periodTo,
//                                          itemsMap }) → { gross, spend, credits }
//
// gross   = Σ total_line_value   over POSTED purchase lines matching scope. GES-362:
//           total_line_value is the amendment-inclusive FINAL-DUE line value
//           (original_line_value + value_delta), so Mode-1 credit_note is already included
//           through the reduced line price and MUST NOT be netted again. Fallback:
//           qty × (initial_unit_price ?? unit_price) for legacy rows without the ledger value.
// credits = Σ |amount| of financeData rows with subtype = supplier_credit (Mode-2 only)
//           matching scope+period. Rows are stored negative → take absolute value.
//           Mode-2 is finance-only, so it is not included in total_line_value and must be
//           netted here exactly once.
//           CREDITS HAVE NO ITEM/CATEGORY HANDLE (the finance table carries no item_id /
//           category column — confirmed 124). So when a `category` scope is supplied,
//           credits net NOTHING at that grain (a credit attaches to a supplier/order, not
//           to an item line — MA18 A3). This matches SpendTab's existing rule (supplier
//           credits net the YTD TOTAL, not any single category slice) and keeps the
//           per-category slices == gross while the grand-total / by-supplier grains net.
// spend   = max(0, gross − credits)   (N4 floor — DISPLAY floor only; the money engine
//           returns any excess as a sign-flipped AP debit, NOT this helper's job to
//           redistribute cross-supplier).

// Item category display LABEL — the SAME funnel the tabs use (itemCatKey /
// itemCatLabel across OverviewTab/SpendTab/GitTab, byte-identical). Lifted here so
// category scope resolves in the caller's existing key space (MA18: do NOT re-derive).
export function itemCategoryLabel(item) {
  return item?.item_category_labels?.name || item?.category || 'Other';
}

function inPeriod(d, from, to) {
  const s = d || '';
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
}

export function computeSpend(
  purchases,
  financeData,
  { op = null, supplier = null, category = null, periodFrom = null, periodTo = null, itemsMap = null } = {}
) {
  // GROSS — posted purchase lines in scope, amendment-inclusive FINAL-DUE value (GES-362).
  let gross = 0;
  for (const line of (purchases || [])) {
    if (line.tx_status && line.tx_status !== 'posted') continue;
    if (op != null && line.operation_id !== op) continue;
    if (supplier != null && line.partner_id !== supplier) continue;
    if (category != null && itemsMap) {
      const cat = itemCategoryLabel(itemsMap[line.item_id]);
      if (Array.isArray(category) ? !category.includes(cat) : cat !== category) continue;
    }
    if ((periodFrom || periodTo) && !inPeriod(line.date, periodFrom, periodTo)) continue;
      // 41080a — PREFER THE LEDGER'S OWN LINE VALUE over reconstructing it.
      //
      // qty x initial_unit_price is exact when the price was TYPED, and wrong when it was DERIVED BY
      // DIVISION: a bird line funded at 6 411 250 over 6156 birds stores 1041.46 (numeric(12,2)) and
      // multiplying back gives 6 411 227.76. Measured on prod: two bird lines drifted by 22.24 and
      // 0.96 — the 23.20 that made a supplier's YTD read 14 622 477 instead of 14 622 500. Same root
      // cause as the GIT-invoice divergence db/512 fixed, one layer out: a derived total standing in
      // for a stored one.
      //
      // total_line_value is the ledger's amendment-inclusive FINAL-DUE amount, stamped on each row
      // by GET /api/purchase. Per GES-362, it leads the spend basis: a Mode-1 credit_note already
      // lowers the line price/value here, while Mode-2 supplier_credit is finance-only and is netted
      // below. Never subtract credit_note again or the Mode-1 reduction is double-counted.
      //
      // The multiply remains the fallback for any row the view does not cover, so this can only make
      // a number more exact, never absent.
      const ledgerBase = line.total_line_value;
      if (ledgerBase != null && Number.isFinite(Number(ledgerBase))) {
        gross += Number(ledgerBase);
      } else {
        const base = line.initial_unit_price != null ? line.initial_unit_price : line.unit_price;
        gross += (Number(line.qty) || 0) * (Number(base) || 0);
      }
  }

  // CREDITS — finance credit rows in scope+period. No item/category handle exists on
  // finance, so a category-scoped call means credits do NOT net at this grain (a credit
  // attaches to a supplier/order, never to an item line — MA18 A3). Per-item / per-line
  // GROSS sites call computeGross, which discards credits regardless.
  let credits = 0;
  if (category == null) {
    for (const row of (financeData || [])) {
      if (row.subtype !== 'supplier_credit') continue;
      if (row.tx_status && row.tx_status !== 'posted') continue;
      if (op != null && row.operation_id !== op) continue;
      if (supplier != null && row.partner_id !== supplier) continue;
      if ((periodFrom || periodTo) && !inPeriod(row.date, periodFrom, periodTo)) continue;
      credits += Math.abs(Number(row.amount) || 0);
    }
  }

  const spend = Math.max(0, gross - credits);
  return { gross, spend, credits };
}

// GROSS-only convenience for the per-item breakdown sites (MA18: credits are
// unattributable to an item line). Never subtracts credits.
export function computeGross(purchases, financeData, scope = {}) {
  return computeSpend(purchases, financeData, scope).gross;
}

// ── Order-scoped PRICE ANALYSIS (v6.2 Price Analysis tab) ───────────────────────────────────
// All from the SAME posted `purchases` array SpendTab reads (item_id, partner_id, date, unit_price,
// initial_unit_price). Base price = ex-landed (initial_unit_price ?? unit_price). We compare THIS
// order's unit price for an item against that item's 6-month average AT THIS SUPPLIER and ACROSS
// ALL suppliers — no engine call, no server work.
function _basePrice(p) { const v = p.initial_unit_price != null ? p.initial_unit_price : p.unit_price; return Number(v) || 0; }
function _ymBack(todayStr, monthsBack) {
  // first day of the month `monthsBack` months before today (inclusive window start), YYYY-MM-DD.
  const d = new Date((todayStr || '').slice(0, 10) + 'T00:00:00');
  d.setMonth(d.getMonth() - monthsBack);
  return d.toISOString().slice(0, 10);
}
function _avg(arr) { return arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null; }

// Per order-item comparison rows: { item_id, item_name, thisPrice, supplierAvg, marketAvg,
// vsSupplierPct, vsMarketPct }. `orderItems` = [{ item_id, item_name, thisPrice }].
export function computeOrderPriceRows(purchases, orderItems, supplierId, todayStr, monthsBack = 6, priceOf = _basePrice) {
  const since = _ymBack(todayStr, monthsBack);
  const posted = (purchases || []).filter(p => (!p.tx_status || p.tx_status === 'posted') && (p.date || '') >= since);
  return (orderItems || []).map(it => {
    const forItem = posted.filter(p => p.item_id === it.item_id);
    const supplierAvg = _avg(forItem.filter(p => p.partner_id === supplierId).map(priceOf).filter(v => v != null));
    const marketAvg   = _avg(forItem.map(priceOf).filter(v => v != null));
    const pct = (a, b) => (b == null || b === 0 || a == null) ? null : ((a - b) / b) * 100;
    return {
      item_id: it.item_id, item_name: it.item_name, thisPrice: it.thisPrice,
      supplierAvg, marketAvg,
      vsSupplierPct: pct(it.thisPrice, supplierAvg),
      vsMarketPct:   pct(it.thisPrice, marketAvg),
    };
  });
}

// Monthly base-price series for one item over the last `months` months:
// [{ ym:'2026-02', label:'Feb', supplierAvg, marketAvg }] (nulls where no data).
export function computeItemMonthlyTrend(purchases, itemId, supplierId, todayStr, months = 6, priceOf = _basePrice) {
  const posted = (purchases || []).filter(p => (!p.tx_status || p.tx_status === 'posted') && p.item_id === itemId);
  const base = new Date((todayStr || '').slice(0, 10) + 'T00:00:00');
  const out = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(base); d.setMonth(d.getMonth() - i);
    const ym = d.toISOString().slice(0, 7);
    const inMonth = posted.filter(p => (p.date || '').slice(0, 7) === ym);
    out.push({
      ym,
      label: d.toLocaleDateString('en-GB', { month: 'short' }),
      supplierAvg: _avg(inMonth.filter(p => p.partner_id === supplierId).map(priceOf).filter(v => v != null)),
      marketAvg:   _avg(inMonth.map(priceOf).filter(v => v != null)),
    });
  }
  return out;
}
